import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  structuredData?: object;
  noindex?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://studioasimmete.com/og-image.jpg',
  ogType = 'website',
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  structuredData,
  noindex = false
}: SEOHeadProps) {
  const baseUrl = 'https://studioasimmete.com';
  const fullCanonical = canonical 
    ? `${baseUrl}${canonical.startsWith('/') ? canonical : `/${canonical}`}` 
    : typeof window !== 'undefined' 
      ? `${baseUrl}${window.location.pathname}` 
      : baseUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Remove existing meta tags
    const existingMeta = document.querySelectorAll('meta[data-seo]');
    existingMeta.forEach(meta => meta.remove());

    // Remove existing structured data
    const existingScript = document.querySelectorAll('script[type="application/ld+json"][data-seo]');
    existingScript.forEach(script => script.remove());

    // Create and append meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords || 'iç mimari istanbul, mimari tasarım, studio asım mete, gravitas libera, lüks iç tasarım, minimalist mimari, ofis tasarımı, konut tasarımı, ticari mekan tasarımı' },
      { name: 'author', content: 'Studio Asım Mete' },
      { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: fullCanonical },
      { property: 'og:type', content: ogType },
      { property: 'og:image', content: fullOgImage },
      { property: 'og:site_name', content: 'Studio Asım Mete' },
      { property: 'og:locale', content: 'tr_TR' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: fullOgImage },
      { rel: 'canonical', href: fullCanonical }
    ];

    // Article specific meta tags
    if (ogType === 'article') {
      if (articleAuthor) {
        metaTags.push({ property: 'article:author', content: articleAuthor });
      }
      if (articlePublishedTime) {
        metaTags.push({ property: 'article:published_time', content: articlePublishedTime });
      }
      if (articleModifiedTime) {
        metaTags.push({ property: 'article:modified_time', content: articleModifiedTime });
      }
    }

    // Append meta tags
    metaTags.forEach(tag => {
      const meta = document.createElement(tag.rel ? 'link' : 'meta');
      meta.setAttribute('data-seo', 'true');
      
      if (tag.rel) {
        meta.setAttribute('rel', tag.rel);
        meta.setAttribute('href', tag.href || '');
      } else {
        if (tag.name) {
          meta.setAttribute('name', tag.name);
        }
        if (tag.property) {
          meta.setAttribute('property', tag.property);
        }
        meta.setAttribute('content', tag.content || '');
      }
      
      document.head.appendChild(meta);
    });

    // Append structured data
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(structuredData, null, 2);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      const metaToRemove = document.querySelectorAll('meta[data-seo], link[data-seo], script[type="application/ld+json"][data-seo]');
      metaToRemove.forEach(el => el.remove());
    };
  }, [title, description, keywords, canonical, ogImage, ogType, articleAuthor, articlePublishedTime, articleModifiedTime, structuredData, noindex, fullCanonical, fullOgImage]);

  return null;
}
